import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api, useApi } from '../api/queries'
import type { ResultItem, RulesetView, SeasonSummary, StandingsResponse } from '../api/types'
import type { components } from '../api/schema'
import { DataState } from '../components/DataState'
import { StandingsTable } from '../components/StandingsTable'
import { ResultsMatrix } from '../components/ResultsMatrix'
import { PointsRow, ruleNotes } from '../components/RuleProse'
import { api as client } from '../api/client'
import { SeasonProgression, type Removals } from '../chart/SeasonProgression'

type RacePage = components['schemas']['Page_RaceSummary_']

const TABS = [
  { key: 'drivers', label: 'Drivers' },
  { key: 'constructors', label: 'Constructors' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'rules', label: 'Rules' },
] as const

type TabKey = (typeof TABS)[number]['key']

export function SeasonPage() {
  const { year: raw = '' } = useParams()
  const year = Number(raw)
  const [params, setParams] = useSearchParams()
  const tab = (TABS.find((entry) => entry.key === params.get('tab'))?.key ??
    'drivers') as TabKey

  const season = useApi<SeasonSummary>(['season', year], () =>
    api.GET('/api/v1/seasons/{year}', { params: { path: { year } } }),
  )

  return (
    <section>
      <header className="page-head">
        <h1>{raw}</h1>
        <p className="page-head__aside">
          <Link to={`/f2/season?y=${year}`}>Under Formula 2’s rule →</Link>{' '}
          <Link to={`/f15/season?y=${year}`}>Under Formula 1.5’s rule →</Link>{' '}
          <Link to={`/reverse/season?y=${year}`}>In reverse →</Link>
        </p>
      </header>

      {season.data ? (
        <p className="muted">
          {season.data.drivers_champion ? (
            <>
              Champion: <strong>{season.data.drivers_champion.name}</strong>
            </>
          ) : (
            'No champion recorded.'
          )}
          {season.data.constructors_champion ? (
            <>
              {' · Constructors: '}
              <strong>{season.data.constructors_champion.name}</strong>
            </>
          ) : null}
          {season.data.is_complete ? '' : ' · season in progress'}
        </p>
      ) : null}

      <nav className="tabs">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className={entry.key === tab ? 'tabs__tab tabs__tab--on' : 'tabs__tab'}
            aria-current={entry.key === tab}
            onClick={() => setParams(entry.key === 'drivers' ? {} : { tab: entry.key })}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      {tab === 'drivers' ? <Drivers year={year} /> : null}
      {tab === 'constructors' ? <Standings year={year} championship="constructors" /> : null}
      {tab === 'calendar' ? <Calendar year={year} /> : null}
      {tab === 'rules' ? <Rules year={year} rounds={season.data?.total_rounds} /> : null}
    </section>
  )
}

function useRaces(year: number) {
  return useApi<RacePage>(['races', year], () =>
    api.GET('/api/v1/seasons/{year}/races', {
      params: { path: { year }, query: { limit: 30 } },
    }),
  )
}

/**
 * The standings give the order and the totals; the results fill the grid.
 * `removals` replays the season without them, results included, which is how
 * the Formula 2 and ban pages show their own field. An excluded round loses
 * its column.
 */
export function Drivers({ year, removals: query = {} }: { year: number; removals?: Removals }) {
  const races = useRaces(year)
  const skipped = (query.exclude_rounds ?? '').split(',').filter(Boolean).map(Number)
  const results = useApi<ResultItem[]>(['season-results', year, query], () =>
    api.GET('/api/v1/seasons/{year}/results', { params: { path: { year }, query } }),
  )
  const standings = useApi<StandingsResponse>(['standings', year, 'drivers', query], () =>
    client.GET('/api/v1/seasons/{year}/standings/drivers', {
      params: { path: { year }, query },
    }),
  )

  return (
    <DataState
      query={standings}
      empty={`No drivers’ championship was contested in ${year}.`}
      absent={`No drivers’ championship was contested in ${year}.`}
    >
      {(data) => (
        <>
          <SeasonProgression year={year} championship="drivers" removals={query} />
          {races.data && results.data ? (
            <ResultsMatrix
              items={data.items}
              races={races.data.items.filter((race) => !skipped.includes(race.round))}
              results={results.data}
            />
          ) : (
            <StandingsTable items={data.items} championship="drivers" rounds={false} />
          )}
        </>
      )}
    </DataState>
  )
}

export function Standings({
  year,
  championship,
  removals: query = {},
}: {
  year: number
  championship: 'drivers' | 'constructors'
  removals?: Removals
}) {
  const standings = useApi<StandingsResponse>(['standings', year, championship, query], () =>
    championship === 'drivers'
      ? client.GET('/api/v1/seasons/{year}/standings/drivers', {
          params: { path: { year }, query },
        })
      : client.GET('/api/v1/seasons/{year}/standings/constructors', {
          params: { path: { year }, query },
        }),
  )

  return (
    <DataState
      query={standings}
      empty={`No ${championship}’ championship was contested in ${year}.`}
      absent={
        championship === 'constructors'
          ? `There was no constructors’ championship in ${year}. The title was first awarded in 1958.`
          : `No drivers’ championship was contested in ${year}.`
      }
    >
      {(data) => (
        <>
          <SeasonProgression year={year} championship={championship} removals={query} />
          <StandingsTable items={data.items} championship={championship} rounds={false} />
        </>
      )}
    </DataState>
  )
}

function Calendar({ year }: { year: number }) {
  const races = useRaces(year)

  return (
    <DataState query={races} empty={`No races are recorded for ${year}.`}>
      {(page) => (
        <table className="grid">
          <thead>
            <tr>
              <th className="num">Rd</th>
              <th>Grand Prix</th>
              <th>Circuit</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {[...page.items]
              .sort((a, b) => a.round - b.round)
              .map((race) => (
                <tr key={race.round}>
                  <td className="num">{race.round}</td>
                  <td>
                    {race.has_results ? (
                      <Link to={`/races/${year}/${race.round}`}>{race.grand_prix.name}</Link>
                    ) : (
                      <span className="muted">{race.grand_prix.name} · to come</span>
                    )}
                    {race.has_sprint ? <span className="badge">sprint</span> : null}
                  </td>
                  <td className="muted">{race.circuit.name}</td>
                  <td className="muted">{race.date}</td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </DataState>
  )
}

function Rules({ year, rounds }: { year: number; rounds: number | undefined }) {
  const rules = useApi<RulesetView[]>(['rules', year], () =>
    api.GET('/api/v1/seasons/{year}/rules', { params: { path: { year } } }),
  )
  const schemes = useApi<components['schemas']['PointSchemeView'][]>(['points-systems'], () =>
    api.GET('/api/v1/points-systems', {}),
  )

  return (
    <DataState query={rules} empty={`No rules are recorded for ${year}.`}>
      {(sets) => (
        <div className="rules">
          {sets.map((ruleset) => (
            <article key={ruleset.championship} className="rules__set">
              <h3>{ruleset.championship === 'DRIVERS' ? 'Drivers' : 'Constructors'}</h3>
              <ul className="rules__notes">
                {ruleNotes(ruleset, rounds).map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
              {Object.entries(ruleset.point_schemes).map(([session, key]) => {
                const scheme = (schemes.data ?? []).find((entry) => entry.key === key)
                return scheme ? (
                  <div key={session}>
                    <p className="muted rules__session">
                      {session.toLowerCase().replace('_', ' ')}
                    </p>
                    <PointsRow scheme={scheme} />
                  </div>
                ) : null
              })}
              {(ruleset.segments ?? []).length > 0 ? (
                <p className="muted">
                  Split season:{' '}
                  {ruleset.segments
                    ?.map(
                      (segment) =>
                        `rounds ${segment.from_round}–${segment.to_round}, best ${segment.best_n}`,
                    )
                    .join('; ')}
                  .
                </p>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </DataState>
  )
}
